import { getBaseRole } from '@/helpers/customRoles';
import { ColumnDef, Row } from '@tanstack/react-table'
import { useMemo } from 'react'
import { EditOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import {
  IconButtonCell,
  TextCell,
  UserCardCell,
  UserCell,
} from '@pages/Admin/Reports/Payments/components/Cells/Cells'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import { IPrivateAllLessonItem } from '../../utils/types'
import useHideStudentsPersonalDataForTeacher from '@/hooks/useHideStudentsPersonalDataForTeacher'
import { useFranchises } from '@/hooks/useFranchises'

interface TableColumnsProps {
  onEdit: (report: IPrivateAllLessonItem) => void
  canOpenSeminarianCards: boolean
  onStudentClick: (user: IUser) => void
  onSeminarianClick: (user: IUser) => void
}

const usePrivateAllLessonsColumns = ({
  onEdit,
  canOpenSeminarianCards,
  onStudentClick,
  onSeminarianClick,
}: TableColumnsProps) => {
  const settings = useSelector((store: any) => ({
    teacherCanEdit: store.settings?.isTeacherCanEditStatuses === '1',
    role: store.profile?.profile?.role,
  }))
  const hideStudentsPersonalData = useHideStudentsPersonalDataForTeacher()
  const { enabled: franchisesEnabled } = useFranchises()

  return useMemo<ColumnDef<IPrivateAllLessonItem>[]>(() => {
    const isTeacher = ['seminarian', 'senior-seminarian'].includes(
      getBaseRole(settings.role),
    )
    const canEdit = !isTeacher || settings.teacherCanEdit

    const allColumns: ColumnDef<IPrivateAllLessonItem>[] = [
      {
        header: '',
        id: 'index',
        size: 40,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) => (
          <TextCell
            text={String(row.original.index ?? row.index + 1)}
            styles={{ textAlign: 'center' }}
          />
        ),
      },
      {
        header: 'Дата и время занятия',
        id: 'date-time',
        size: 150,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) => (
          <TextCell text={row.original.date_time ?? '-'} />
        ),
      },
      {
        header: 'Педагог',
        id: 'seminarian',
        size: 180,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) => (
          <UserCardCell
            user={row.original.seminarian}
            onClick={
              canOpenSeminarianCards
                ? () => onSeminarianClick(row.original.seminarian)
                : undefined
            }
          />
        ),
      },
      {
        header: 'Ученик',
        id: 'student',
        size: 180,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) => (
          <UserCell
            name={row.original.student?.name ?? '-'}
            icon={row.original.student?.avatar_path}
            styles={{
              textDecoration: 'underline',
            }}
            onClick={() => onStudentClick(row.original.student)}
          />
        ),
      },
      {
        header: 'Филиал',
        id: 'franchise',
        size: 180,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) => (
          <TextCell text={row.original.franchise?.name ?? '-'} />
        ),
      },
      {
        header: 'Курс',
        id: 'course',
        size: 180,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) =>
          row.original.is_course_deleted ? (
            <TooltipWrapper title="Курс у ученика удалён. ЗП и цена считаются по ставкам удалённого заказа, платная отмена недоступна.">
              <TextCell
                text={`${row.original.course_name ?? '-'} (удалён у ученика)`}
              />
            </TooltipWrapper>
          ) : (
            <TextCell text={row.original.course_name ?? '-'} />
          ),
      },
      {
        header: 'Длительность',
        id: 'duration',
        size: 90,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) => (
          <TextCell text={String(row.original.duration ?? '-')} />
        ),
      },
      {
        header: 'Статус',
        id: 'status',
        size: 120,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) => (
          <TextCell
            text={row.original.status_text ?? '-'}
            wrapperStyles={{
              backgroundColor: getStatusColor(row.original.status),
            }}
          />
        ),
      },
      {
        header: 'Цена занятия',
        id: 'price',
        size: 110,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) => (
          <TextCell text={String(row.original.price ?? '-')} />
        ),
      },
      {
        header: 'ЗП',
        id: 'salary',
        size: 110,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) => (
          <TextCell text={String(row.original.salary ?? '-')} />
        ),
      },
      {
        header: 'Комментарий',
        id: 'comment',
        size: 180,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) => (
          <TooltipWrapper title={row.original.comment ?? ''}>
            <TextCell text={row.original.comment ?? '-'} />
          </TooltipWrapper>
        ),
      },
      {
        header: '',
        id: 'edit',
        size: 50,
        cell: ({ row }: { row: Row<IPrivateAllLessonItem> }) => (
          <IconButtonCell
            icon={
              <EditOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            }
            onClick={() => onEdit(row.original)}
          />
        ),
      },
    ]

    return allColumns.filter((item) => {
      if (item.id === 'franchise' && !franchisesEnabled) {
        return false
      }
      if (item.id === 'price') {
        return !isTeacher
      }
      if (item.id === 'edit') {
        return canEdit
      }
      return true
    })
  }, [
    settings,
    onEdit,
    canOpenSeminarianCards,
    onStudentClick,
    onSeminarianClick,
    franchisesEnabled,
  ])
}

const getStatusColor = (status: number) => {
  switch (status) {
    case 5:
      return 'var(--color-status-success-border)'
    case 10:
    case 11:
      return 'var(--color-status-danger-border)'
    case 12:
      return 'var(--color-status-danger-bg)'
    case 9:
      return 'var(--color-status-warning)'
    default:
      return 'var(--color-status-warning-border)'
  }
}

export default usePrivateAllLessonsColumns
